import { useEffect, useState } from "react";

import { state } from "../state/workspaceStore.js";

import { clone, product, id, save } from "../utils/domain/model.js";

import { emptyProduct } from "../data/catalogue.js";

import { notFound } from "./NotFoundPage.jsx";

import { head } from "../components/common/PageHeader.jsx";

import {
  btn,
  field,
  select,
  area,
  check,
  errorSlot,
} from "../components/forms/FormControls.jsx";

import { image } from "../components/products/ProductImage.jsx";

import { nodes } from "../components/common/reactHelpers.jsx";

import { esc, num } from "../utils/format.js";

import { tableViews } from "../components/tables/RecordTable.jsx";

import { sellingPrice } from "../utils/domain/operations.js";

const PRODUCT_API = "https://api.praachinorganics.com/api/products";
const COLLECTION_API = "https://api.praachinorganics.com/api/Collections";
const API_BASE_URL = "https://api.praachinorganics.com";

function getProductImageUrl(imageUrl) {
  if (!imageUrl) {
    return "";
  }

  if (/^data:image\//i.test(imageUrl)) {
    return imageUrl;
  }

  if (/^https?:\/\//i.test(imageUrl)) {
    return imageUrl;
  }

  return `${API_BASE_URL.replace(/\/$/, "")}/${imageUrl.replace(/^\/+/, "")}`;
}

function statusToApi(value) {
  return (
    {
      Draft: 1,

      Active: 2,

      "Coming Soon": 3,

      "Out of Stock": 4,
    }[String(value || "").trim()] || 1
  );
}

function statusFromApi(value) {
  if (value === "Draft") {
    return "Draft";
  }

  if (value === "Active") {
    return "Active";
  }

  if (value === "ComingSoon" || value === "Coming Soon") {
    return "Coming Soon";
  }

  if (value === "OutOfStock" || value === "Out of Stock") {
    return "Out of Stock";
  }

  return (
    {
      1: "Draft",

      2: "Active",

      3: "Coming Soon",

      4: "Out of Stock",
    }[Number(value)] || "Draft"
  );
}

/* =========================================================

   QUANTITY UNIT

\========================================================= */

function unitToApi(value) {
  return (
    {
      g: 1,

      kg: 2,

      ml: 3,

      l: 4,
    }[
    String(value || "")
      .trim()

      .toLowerCase()
    ] || 1
  );
}

function unitFromApi(value) {
  if (value === "Gram") {
    return "g";
  }

  if (value === "Kilogram") {
    return "kg";
  }

  if (value === "Millilitre") {
    return "ml";
  }

  if (value === "Litre") {
    return "l";
  }

  return (
    {
      1: "g",

      2: "kg",

      3: "ml",

      4: "l",
    }[Number(value)] || "g"
  );
}

/* =========================================================

   HELPERS

\========================================================= */

function nullableNumber(value) {
  if (value === "" || value == null) {
    return null;
  }

  const n = Number(value);

  return Number.isFinite(n) ? n : null;
}

function parseDimensions(value) {
  const parts = String(value || "")
    .split(/[x×]/i)

    .map((x) => Number(String(x).trim()));

  return {
    lengthCm: Number.isFinite(parts[0]) ? parts[0] : null,

    widthCm: Number.isFinite(parts[1]) ? parts[1] : null,

    heightCm: Number.isFinite(parts[2]) ? parts[2] : null,
  };
}

function apiError(
  data,

  status,
) {
  if (data?.errors) {
    return Object.values(data.errors)

      .flat()

      .join("\n");
  }

  return (
    data?.message ||
    data?.title ||
    (typeof data === "string" ? data : "") ||
    `API request failed with status ${status}.`
  );
}

/* =========================================================

   COMMON API REQUEST

\========================================================= */

async function request(url, options = {}) {
  const isFormData = options.body instanceof FormData;

  const headers = {
    Accept: "application/json",
    ...(options.headers || {}),
  };

  // JSON requests need this header. FormData requests must not set it
  // manually because the browser supplies the multipart boundary.
  if (options.body && !isFormData) {
    headers["Content-Type"] = "application/json";
  }

  console.log("API CALL:", options.method || "GET", url);

  try {
    const response = await fetch(url, {
      ...options,
      headers,
    });

    const contentType = response.headers.get("content-type") || "";
    let data = null;

    try {
      data = contentType.includes("application/json")
        ? await response.json()
        : await response.text();
    } catch (readError) {
      console.error("API RESPONSE READ ERROR:", readError);
    }

    console.log("API STATUS:", response.status, response.statusText);
    console.log("API RESPONSE:", data);

    if (!response.ok) {
      throw new Error(apiError(data, response.status));
    }

    return data;
  } catch (error) {
    console.error("API REQUEST FAILED:", error);
    console.error("REQUEST URL:", url);

    if (error instanceof TypeError) {
      throw new Error(
        "Unable to connect to the backend API. Verify that the backend is running, its HTTPS certificate is trusted, and CORS is enabled.",
      );
    }

    throw error;
  }
}

/* =========================================================

   COLLECTION API RESPONSE -> UI MODEL

\========================================================= */

function mapCollection(item) {
  return {
    id: Number(item?.id ?? item?.Id ?? 0),

    name: item?.name ?? item?.Name ?? "",

    code: item?.code ?? item?.Code ?? "",

    status: item?.status ?? item?.Status ?? null,

    statusName: item?.statusName ?? item?.StatusName ?? "",

    displayOrder: Number(item?.displayOrder ?? item?.DisplayOrder ?? 0),

    description: item?.description ?? item?.Description ?? "",

    productCount: Number(item?.productCount ?? item?.ProductCount ?? 0),

    orderCount: Number(item?.orderCount ?? item?.OrderCount ?? 0),

    archived: false,
  };
}

/* =========================================================

   PRODUCT API RESPONSE -> UI MODEL

\========================================================= */

function mapApiProduct(
  item,

  collections,
) {
  const variant = item?.variant || item?.Variant || {};

  const collectionId = item?.collectionId ?? item?.CollectionId ?? null;

  const collectionName = item?.collectionName ?? item?.CollectionName ?? "";

  const selectedCollection =
    collectionId != null
      ? collections.find(
        (collection) => String(collection.id) === String(collectionId),
      )
      : collections.find(
        (collection) =>
          String(collection.name || "")
            .trim()
            .toLowerCase() ===
          String(collectionName || "")
            .trim()
            .toLowerCase(),
      );

  const imageData = item?.image || item?.Image || null;

  const imageUrl = imageData?.imageUrl ?? imageData?.ImageUrl ?? null;

  return {
    id: String(item?.id ?? item?.Id ?? ""),

    name: item?.name ?? item?.Name ?? "",

    collection:
      collectionId != null
        ? String(collectionId)
        : String(selectedCollection?.id || ""),

    collectionName: collectionName || selectedCollection?.name || "",

    variant: variant?.variantName ?? variant?.VariantName ?? "",

    status: statusFromApi(item?.status ?? item?.Status),

    description: item?.description ?? item?.Description ?? "",

    photos: imageUrl ? [imageUrl] : [],

    skus: [
      {
        id: String(variant?.id ?? variant?.Id ?? ""),

        code: variant?.skuCode ?? variant?.SkuCode ?? "",

        variant: variant?.variantName ?? variant?.VariantName ?? "",

        qty: variant?.netQuantity ?? variant?.NetQuantity ?? "",

        unit: unitFromApi(variant?.unit ?? variant?.Unit),

        mrp: variant?.mrp ?? variant?.Mrp ?? "",

        discount:
          variant?.discountPercentage ?? variant?.DiscountPercentage ?? 0,

        price: variant?.sellingPrice ?? variant?.SellingPrice ?? "",

        stock: variant?.stock ?? variant?.Stock ?? 0,

        threshold:
          variant?.lowStockThreshold ?? variant?.LowStockThreshold ?? 0,

        weight: variant?.packedWeightGrams ?? variant?.PackedWeightGrams ?? "",

        dimensions: [
          variant?.lengthCm ?? variant?.LengthCm,

          variant?.widthCm ?? variant?.WidthCm,

          variant?.heightCm ?? variant?.HeightCm,
        ]

          .filter((x) => x !== null && x !== undefined)

          .join(" × "),
      },
    ],

    ingredients: item?.ingredients ?? item?.Ingredients ?? "",

    nutrition: item?.nutrition ?? item?.Nutrition ?? "",

    allergens: item?.allergenDeclaration ?? item?.AllergenDeclaration ?? "",

    shelfLife: item?.shelfLife ?? item?.ShelfLife ?? "",

    storage: item?.storageInstructions ?? item?.StorageInstructions ?? "",

    manufacturer: [
      item?.manufacturerName ?? item?.ManufacturerName,

      item?.manufacturerAddress ?? item?.ManufacturerAddress,
    ]

      .filter(Boolean)

      .join("\n"),

    marketer: [
      item?.marketerName ?? item?.MarketerName,

      item?.marketerAddress ?? item?.MarketerAddress,
    ]

      .filter(Boolean)

      .join("\n"),

    fssai: item?.fssaiLicenseDetails ?? item?.FssaiLicenseDetails ?? "",

    infoMode:
      item?.informationProvenance ?? item?.InformationProvenance ?? "reference",

    taxRate: item?.gstRate ?? item?.GstRate ?? null,

    complianceVerified: Boolean(
      item?.labelComplianceReviewed ?? item?.LabelComplianceReviewed,
    ),

    labelMatch: false,

    stockReason: "",

    archived: false,
  };
}

/* =========================================================

   CAPTURE PRODUCT FORM

\========================================================= */

function captureSingleProduct(form) {
  const formData = new FormData(form);

  const productDraft = clone(state.draft);

  for (const key of [
    "name",

    "collection",

    "variant",

    "status",

    "description",

    "ingredients",

    "nutrition",

    "allergens",

    "shelfLife",

    "storage",

    "manufacturer",

    "marketer",

    "fssai",

    "infoMode",
  ]) {
    productDraft[key] = String(formData.get(key) || "").trim();
  }

  productDraft.taxRate =
    formData.get("taxRate") === "" ? null : Number(formData.get("taxRate"));

  productDraft.complianceVerified = formData.has("complianceVerified");

  productDraft.labelMatch = formData.has("labelMatch");

  productDraft.stockReason = String(formData.get("stockReason") || "").trim();

  const sku =
    productDraft.skus?.[0] ||
    emptyProduct(
      id("p"),

      id("s"),
    ).skus[0];

  for (const key of ["code", "variant", "unit", "dimensions"]) {
    sku[key] = String(formData.get(`s0-${key}`) || "").trim();
  }

  for (const key of [
    "qty",

    "mrp",

    "discount",

    "price",

    "stock",

    "threshold",

    "weight",
  ]) {
    sku[key] = num(formData.get(`s0-${key}`));
  }

  sku.price = sellingPrice(
    sku.mrp,

    sku.discount,
  );

  productDraft.skus = [sku];

  return {
    p: productDraft,

    f: formData,
  };
}

/* =========================================================

   CREATE PRODUCT PAYLOAD

\========================================================= */

function buildPayload(
  productDraft,

  collections,
) {
  const sku = productDraft.skus?.[0];

  /* =======================================================

     PRODUCT NAME

  ======================================================= */

  if (!productDraft.name?.trim()) {
    throw new Error("Product name is required.");
  }

  /* =======================================================

     COLLECTION VALIDATION



     Dropdown value = Backend Collection Id

  ======================================================= */

  if (!productDraft.collection) {
    throw new Error("Please select a collection.");
  }

  const selectedCollection = collections.find(
    (collection) => String(collection.id) === String(productDraft.collection),
  );

  if (!selectedCollection) {
    throw new Error("Please select a valid collection.");
  }

  const collectionId = Number(selectedCollection.id);

  if (!Number.isInteger(collectionId) || collectionId <= 0) {
    throw new Error(
      "The selected collection does not have a valid database ID.",
    );
  }

  console.log(
    "SELECTED COLLECTION:",

    selectedCollection,
  );

  console.log(
    "PRODUCT COLLECTION ID:",

    collectionId,
  );

  /* =======================================================

     SKU

  ======================================================= */

  if (!sku?.code?.trim()) {
    throw new Error("SKU code is required.");
  }

  if (!sku?.variant?.trim()) {
    throw new Error("Variant name is required.");
  }

  if (!Number.isFinite(Number(sku.qty)) || Number(sku.qty) <= 0) {
    throw new Error("Net quantity must be greater than zero.");
  }

  /* =======================================================

     STOCK

  ======================================================= */

  const stock = Number(sku.stock || 0);

  if (!Number.isInteger(stock) || stock < 0) {
    throw new Error("Stock must be zero or greater.");
  }

  /* =======================================================

     DIMENSIONS

  ======================================================= */

  const dimensions = parseDimensions(sku.dimensions);

  /* =======================================================

     IMAGE

  ======================================================= */

  const photo = productDraft.photos?.[0] || "";

  const imageUrl = /^https?:\/\//i.test(photo) ? photo : null;

  /* =======================================================

     FINAL CREATE PRODUCT REQUEST

  ======================================================= */

  return {
    name: productDraft.name.trim(),

    collectionId,

    status: statusToApi(productDraft.status),

    description: productDraft.description || null,

    variant: {
      skuCode: sku.code.trim(),

      variantName: sku.variant.trim(),

      netQuantity: Number(sku.qty),

      unit: unitToApi(sku.unit),

      mrp: nullableNumber(sku.mrp),

      discountPercentage: nullableNumber(sku.discount),

      sellingPrice: nullableNumber(sku.price),

      stock,

      lowStockThreshold: nullableNumber(sku.threshold),

      packedWeightGrams: nullableNumber(sku.weight),

      lengthCm: dimensions.lengthCm,

      widthCm: dimensions.widthCm,

      heightCm: dimensions.heightCm,

      stockChangeReason:
        stock > 0 ? productDraft.stockReason || "Initial stock added" : null,
    },

    image: imageUrl
      ? {
        imageUrl,
      }
      : null,

    informationProvenance: productDraft.infoMode || null,

    gstRate: nullableNumber(productDraft.taxRate),

    ingredients: productDraft.ingredients || null,

    nutrition: productDraft.nutrition || null,

    allergenDeclaration: productDraft.allergens || null,

    shelfLife: productDraft.shelfLife || null,

    storageInstructions: productDraft.storage || null,

    fssaiLicenseDetails: productDraft.fssai || null,

    manufacturerName: productDraft.manufacturer || null,

    manufacturerAddress: null,

    marketerName: productDraft.marketer || null,

    marketerAddress: null,

    labelComplianceReviewed: Boolean(productDraft.complianceVerified),
  };
}

/* =========================================================

   PRODUCT FORM UI

\========================================================= */

export function productForm(
  pid,

  collections = [],

  onSubmit,

  loadingCollections = false,

  saving = false,

  loadError = "",
) {
  const key = "product-" + (pid || "new");

  /* =======================================================

     DRAFT

  ======================================================= */

  if (state.draftKey !== key) {
    state.draft = pid
      ? clone(
        product(
          state.db,

          pid,
        ),
      )
      : emptyProduct(
        id("p"),

        id("s"),
      );

    state.draftKey = key;
  }

  const p = state.draft;

  if (!p) {
    return notFound();
  }

  /* =======================================================

     SINGLE SKU ONLY

  ======================================================= */

  if (!Array.isArray(p.skus) || !p.skus.length) {
    p.skus = [
      emptyProduct(
        id("p"),

        id("s"),
      ).skus[0],
    ];
  }

  p.skus = [p.skus[0]];

  const s = p.skus[0];

  /* =======================================================

     COLLECTION DROPDOWN



     value = Backend Id

     text  = Collection Name

  ======================================================= */

  const collectionOptions = [
    [
      "",

      loadingCollections ? "Loading collections..." : "-- Select collection --",
    ],

    ...collections.map((collection) => [
      String(collection.id),

      collection.name,
    ]),
  ];

  const selectedCollection = collections.some(
    (collection) => String(collection.id) === String(p.collection),
  )
    ? String(p.collection)
    : "";

  return (
    <>
      {head(
        "CATALOGUE",

        pid ? "Edit " + p.name : "Add product",

        "Operational fields control availability; compliance review is recorded separately.",

        <>
          {btn(
            "Cancel",

            `data-nav="${pid ? "product" : "products"}" ${pid ? `data-id="${pid}"` : ""
            }`,

            true,
          )}

          <button
            type="submit"
            form="product-form"
            className="btn"
            disabled={loadingCollections || saving || collections.length === 0}
          >
            {saving ? "Saving..." : "Save"}
          </button>
        </>,
      )}

      <form id="product-form" className="product-form-full" onSubmit={onSubmit}>
        <div className="stack">
          {/* =================================================

              PRODUCT IDENTITY

          ================================================= */}

          <section className="card">
            <h2>Product identity</h2>

            <div className="form-grid">
              {field(
                "Product name \*",

                "name",

                p.name,

                "text",

                'required maxlength="120"',
              )}

              {select(
                "Collection \*",

                "collection",

                selectedCollection,

                collectionOptions,
              )}

              {field(
                "Primary variant / flavour",

                "variant",

                p.variant,
              )}

              {select(
                "Status",

                "status",

                p.status,

                ["Draft", "Active", "Coming Soon", "Out of Stock"],
              )}
            </div>

            {loadingCollections ? (
              <p className="hint">Loading collections...</p>
            ) : null}

            {!loadingCollections && !loadError && collections.length === 0 ? (
              <p className="error">No collections are available.</p>
            ) : null}

            {loadError ? (
              <p className="error" role="alert">
                {loadError}
              </p>
            ) : null}

            {area(
              "Description \*",

              "description",

              p.description,
            )}

            {/* =================================================

                PRODUCT IMAGE

            ================================================= */}

            <div className="product-image-editor">
              <h3>Add Product Image</h3>

              {p.photos.length ? (
                <div className="photo-strip">
                  {nodes(
                    p.photos.map(
                      (
                        src,

                        index,
                      ) => (
                        <div key={`${src}-${index}`}>
                          <img
                            src={getProductImageUrl(src)}
                            alt={`${p.name} photo ${index + 1}`}
                            style={{
                              width: "180px",
                              height: "180px",
                              objectFit: "contain",
                              borderRadius: "8px",
                            }}
                          />

                          <button
                            type="button"
                            className="text-btn danger"
                            data-remove-photo={index}
                          >
                            Remove
                          </button>
                        </div>
                      ),
                    ),
                  )}
                </div>
              ) : (
                ""
              )}

              <label className="field space-top">
                <span>Add Product Image · JPEG, PNG or WebP, max 2 MB</span>

                <input
                  id="product-image"
                  name="Image"
                  type="file"
                  accept="image/jpeg,image/png,image/webp"
                />
              </label>

              {p.photos.length
                ? btn(
                  "Replace main image",

                  'data-action="replace-product-image"',

                  true,
                )
                : ""}

              {check(
                "The uploaded label matches this product",

                "labelMatch",

                p.labelMatch || false,
              )}

              <p id="image-error" className="error" role="alert"></p>

              <p className="hint" role="status">
                {esc(p.imageFeedback || "Images are staged until Save.")}
              </p>

              <p className="hint">
                Cultured Ghee and Mint Millet Bhujiya remain unmatched and
                excluded. Image fallback is used when no correct pack is
                available.
              </p>
            </div>
          </section>

          {/* =================================================

              VARIANT & STOCK

          ================================================= */}

          <section className="card">
            <div className="card-head">
              <h2>Variant & stock</h2>
            </div>

            <div className="sku-block">
              <div className="form-grid three">
                {field(
                  "SKU code \*",

                  "s0-code",

                  s.code,

                  "text",

                  "required",
                )}

                {field(
                  "Variant \*",

                  "s0-variant",

                  s.variant,

                  "text",

                  "required",
                )}

                {field(
                  "Net quantity \*",

                  "s0-qty",

                  s.qty,

                  "number",

                  'min="0.01" step="any" required',
                )}

                {select(
                  "Unit \*",

                  "s0-unit",

                  s.unit,

                  ["g", "kg", "ml", "l"],
                )}

                {field(
                  "MRP ₹ \*",

                  "s0-mrp",

                  s.mrp,

                  "number",

                  'min="0" step="0.01" required',
                )}

                {field(
                  "Discount % \*",

                  "s0-discount",

                  s.discount ?? 0,

                  "number",

                  'min="0" max="100" step="any" required',
                )}

                {field(
                  "Selling price ₹ \*",

                  "s0-price",

                  s.price,

                  "number",

                  'min="0" step="0.01" readonly required',
                )}

                {field(
                  "Stock \*",

                  "s0-stock",

                  s.stock,

                  "number",

                  'min="0" step="1" required',
                )}

                {field(
                  "Low-stock threshold \*",

                  "s0-threshold",

                  s.threshold,

                  "number",

                  'min="0" step="1" required',
                )}

                {field(
                  "Packed weight (g) \*",

                  "s0-weight",

                  s.weight,

                  "number",

                  'min="0.01" step="any" required',
                )}

                {field(
                  "Dimensions (cm) \*",

                  "s0-dimensions",

                  s.dimensions,

                  "text",

                  'placeholder="10 × 10 × 15" required',
                )}
              </div>
            </div>

            <p className="hint">
              Changes to existing stock are recorded with a reason. Use Adjust
              stock for regular inventory updates.
            </p>

            {field(
              "Reason for stock change",

              "stockReason",

              p.stockReason || "",

              "text",
            )}
          </section>

          {/* =================================================

              FOOD / LABEL

          ================================================= */}

          <section className="card">
            <h2>Food and label information</h2>

            <div className="form-grid">
              {select(
                "Information provenance",

                "infoMode",

                p.infoMode,

                ["reference", "verified"],
              )}

              {field(
                "GST rate (%) — optional",

                "taxRate",

                p.taxRate,

                "number",

                'min="0" max="100" step="any"',
              )}
            </div>

            {area(
              "Ingredients",

              "ingredients",

              p.ingredients,
            )}

            {area(
              "Nutrition",

              "nutrition",

              p.nutrition,
            )}

            <div className="form-grid">
              {field(
                "Allergen declaration",

                "allergens",

                p.allergens,
              )}

              {field(
                "Shelf life",

                "shelfLife",

                p.shelfLife,
              )}

              {field(
                "Storage instructions",

                "storage",

                p.storage,
              )}

              {field(
                "FSSAI licence details — unverified until reviewed",

                "fssai",

                p.fssai,
              )}
            </div>

            {area(
              "Manufacturer name and address",

              "manufacturer",

              p.manufacturer,
            )}

            {area(
              "Marketer name and address",

              "marketer",

              p.marketer,
            )}

            {check(
              "Label/compliance information reviewed",

              "complianceVerified",

              p.complianceVerified,
            )}

            <p className="hint">
              Active is an internal catalogue status. This project has no public
              publishing endpoint.
            </p>
          </section>

          {errorSlot()}
        </div>
      </form>
    </>
  );
}

/* =========================================================

   PRODUCT FORM PAGE

\========================================================= */

export default function ProductFormPage({ recordId } = {}) {
  const [collections, setCollections] = useState([]);

  const [loadingCollections, setLoadingCollections] = useState(true);

  const [loadingProduct, setLoadingProduct] = useState(Boolean(recordId));

  const [loadError, setLoadError] = useState("");

  const [saving, setSaving] = useState(false);

  const [, setRevision] = useState(0);

  /* =======================================================
     LOAD COLLECTIONS + PRODUCT FOR EDIT
  ======================================================= */

  useEffect(() => {
    let active = true;

    async function loadPage() {
      setLoadingCollections(true);
      setLoadingProduct(Boolean(recordId));
      setLoadError("");

      try {
        const collectionResponse = await request(COLLECTION_API);

        if (!Array.isArray(collectionResponse)) {
          throw new Error("The Collections API returned an invalid response.");
        }

        const mappedCollections = collectionResponse
          .map(mapCollection)
          .filter(
            (collection) =>
              Number.isInteger(collection.id) && collection.id > 0,
          )
          .sort((a, b) => a.displayOrder - b.displayOrder);

        if (!active) {
          return;
        }

        state.db.collections = mappedCollections;

        setCollections(mappedCollections);

        /* ===============================================
           EDIT MODE:
           GET /api/products/{id}
        =============================================== */

        if (recordId) {
          const productId = Number(recordId);

          if (!Number.isInteger(productId) || productId <= 0) {
            throw new Error("A valid product ID is required for editing.");
          }

          const apiProduct = await request(`${PRODUCT_API}/${productId}`);

          if (!active) {
            return;
          }

          const mappedProduct = mapApiProduct(apiProduct, mappedCollections);

          if (!mappedProduct.id) {
            throw new Error("The Product API returned an invalid product.");
          }

          const existingIndex = state.db.products.findIndex(
            (item) => String(item.id) === String(mappedProduct.id),
          );

          if (existingIndex < 0) {
            state.db.products.push(mappedProduct);
          } else {
            state.db.products[existingIndex] = mappedProduct;
          }

          save(localStorage, state.db);

          /*
            Fill Edit Product form with backend values.
          */
          state.draft = clone(mappedProduct);

          state.draftKey = `product-${recordId}`;

          setRevision((n) => n + 1);
        }
      } catch (error) {
        console.error("PRODUCT FORM LOAD FAILED:", error);

        if (active) {
          setLoadError(error?.message || "Unable to load the product form.");
        }
      } finally {
        if (active) {
          setLoadingCollections(false);
          setLoadingProduct(false);
        }
      }
    }

    loadPage();

    return () => {
      active = false;
    };
  }, [recordId]);

  /* =======================================================
     ADD / UPDATE PRODUCT

     Add:
       POST /api/products

     Edit:
       PUT /api/products/{id}

     Edit NEVER calls POST.
  ======================================================= */

  async function handleProductSubmit(event) {
    event.preventDefault();
    event.stopPropagation();

    const errorElement = document.getElementById("form-error");

    if (errorElement) {
      errorElement.textContent = "";
    }

    setSaving(true);

    try {
      if (loadingCollections || loadingProduct) {
        throw new Error("Please wait while the product form is loading.");
      }

      if (collections.length === 0) {
        throw new Error(
          "No collections are available. A product cannot be saved without a collection.",
        );
      }

      const { p, f } = captureSingleProduct(event.currentTarget);

      const form = event.currentTarget;

      const imageInput = form.querySelector('input[type="file"][name="Image"]');

      const imageFile = imageInput?.files?.[0] ?? null;

      const labelMatchInput = form.querySelector('input[name="labelMatch"]');

      const labelMatchConfirmed = labelMatchInput
        ? labelMatchInput.checked
        : f.has("labelMatch");

      // Require confirmation only when uploading a new image.
      if (imageFile && !labelMatchConfirmed) {
        throw new Error(
          "Please confirm that the uploaded packaging label matches this product.",
        );
      }

      const payload = buildPayload(p, collections);

      const isEdit = Boolean(recordId);

      let url = PRODUCT_API;

      let method = "POST";

      if (isEdit) {
        const productId = Number(recordId);

        if (!Number.isInteger(productId) || productId <= 0) {
          throw new Error("A valid product ID is required for updating.");
        }

        url = `${PRODUCT_API}/${productId}`;
        method = "PUT";
      }

      const formData = new FormData();

      function appendValue(key, value) {
        if (value === undefined || value === null) {
          return;
        }

        if (typeof value === "object") {
          throw new Error(`Unexpected object for form field "${key}".`);
        }

        formData.append(key, String(value));
      }

      appendValue("Name", payload.name);
      appendValue("CollectionId", payload.collectionId);
      appendValue("Status", payload.status);
      appendValue("Description", payload.description);

      appendValue("InformationProvenance", payload.informationProvenance);
      appendValue("GstRate", payload.gstRate);
      appendValue("Ingredients", payload.ingredients);
      appendValue("Nutrition", payload.nutrition);
      appendValue("AllergenDeclaration", payload.allergenDeclaration);
      appendValue("ShelfLife", payload.shelfLife);
      appendValue("StorageInstructions", payload.storageInstructions);
      appendValue("FssaiLicenseDetails", payload.fssaiLicenseDetails);
      appendValue("ManufacturerName", payload.manufacturerName);
      appendValue("ManufacturerAddress", payload.manufacturerAddress);
      appendValue("MarketerName", payload.marketerName);
      appendValue("MarketerAddress", payload.marketerAddress);
      appendValue("LabelComplianceReviewed", payload.labelComplianceReviewed);

      const variant = payload.variant;

      if (!variant) {
        throw new Error("Product variant information is required.");
      }

      appendValue("Variant.SkuCode", variant.skuCode);
      appendValue("Variant.VariantName", variant.variantName);
      appendValue("Variant.NetQuantity", variant.netQuantity);
      appendValue("Variant.Unit", variant.unit);
      appendValue("Variant.Mrp", variant.mrp);
      appendValue("Variant.DiscountPercentage", variant.discountPercentage);
      appendValue("Variant.SellingPrice", variant.sellingPrice);
      appendValue("Variant.Stock", variant.stock);
      appendValue("Variant.LowStockThreshold", variant.lowStockThreshold);
      appendValue("Variant.PackedWeightGrams", variant.packedWeightGrams);
      appendValue("Variant.LengthCm", variant.lengthCm);
      appendValue("Variant.WidthCm", variant.widthCm);
      appendValue("Variant.HeightCm", variant.heightCm);

      if (variant.stockChangeReason != null) {
        appendValue("Variant.StockChangeReason", variant.stockChangeReason);
      }

      // Only send Image when the user actually selected a file.
      // Omitting it during edit preserves the existing image,
      // provided the backend update service is implemented that way.
      if (imageFile) {
        formData.append("Image", imageFile, imageFile.name);
      }

      console.log(isEdit ? "UPDATE PRODUCT:" : "CREATE PRODUCT:", url);

      const savedProduct = await request(url, {
        method,
        body: formData,
      });

      console.log(
        isEdit ? "PRODUCT UPDATED:" : "PRODUCT CREATED:",
        savedProduct,
      );

      const mappedProduct = mapApiProduct(savedProduct, collections);

      if (!mappedProduct.id && recordId) {
        mappedProduct.id = String(recordId);
      }

      if (mappedProduct.id) {
        const existingIndex = state.db.products.findIndex(
          (item) => String(item.id) === String(mappedProduct.id),
        );

        if (existingIndex < 0) {
          state.db.products.unshift(mappedProduct);
        } else {
          state.db.products[existingIndex] = mappedProduct;
        }

        save(localStorage, state.db);
      }

      state.draft = null;
      state.draftKey = "";
      state.formBaseline = null;
      state.navigationApproved = true;

      location.hash = "#products";
    } catch (error) {
      console.error(
        recordId ? "PRODUCT UPDATE FAILED:" : "PRODUCT CREATE FAILED:",
        error,
      );

      if (errorElement) {
        errorElement.textContent =
          error?.message ||
          (recordId
            ? "Unable to update the product."
            : "Unable to create the product.");

        errorElement.focus();
      }
    } finally {
      setSaving(false);
    }
  }

  /*
    For edit, wait until GET /api/products/{id}
    has loaded the latest backend record.
  */
  if (recordId && loadingProduct) {
    return tableViews(
      <>
        {head(
          "CATALOGUE",
          "Loading product...",
          "Loading the latest product data from the backend.",
          <></>,
        )}

        <section className="card">
          <p className="hint">Loading product...</p>
        </section>
      </>,
    );
  }

  return tableViews(
    productForm(
      recordId,
      collections,
      handleProductSubmit,
      loadingCollections,
      saving,
      loadError,
    ),
  );
}
